import React, { useState, useEffect, useRef } from "react";
import {
  ErrorCatchMsg,
  MainTitle,
  CommonBusinessRulesList,
} from "../../Utils/Constants.";
import { Box, CircularProgress } from "@mui/material";
import { ErrorHandler, NoDataFoundComp } from "../../Utils/Constants.";
import useAuth from "../../Utils/useAuth";
import networkConfigServices from "../../services/networkConfigServices";
import DropdownItem from "./DropdownItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import SearchInputComp from "../../components/SharedComp/SearchInputComp";
import InfiniteScroll from "react-infinite-scroll-component";
import AddEditNetwork from "./AddEditNetwork";
import { withRouter } from "react-router-dom";
import ConfirmDialog, { DEACTIVATE_COPY } from "../../components/SharedComp/ConfirmDialog";
import DesignModal from "../../components/SharedComp/DesignModal";
import { classify, formFeedback, DESIGN_TEXT, OUTCOME } from "../../Utils/e5Messages";
import useDebouncedCallback, { SEARCH_DEBOUNCE_MS } from "../../Utils/useDebouncedCallback";

// fields on the Network form (400 errors for anything else show "Your changes were not saved.")
const NETWORK_FORM_FIELDS = ["networkName", "networkNumber", "mediaFieBasePath"];
/**
//    * @component  Network Configuration Component
//    * @description  Network configuration details component to list out, add and edit network
//    * @returns Network Configuration  UI
//    */
const NetworkConfiguration = (props) => {
  const { bearerToken, localStorageData } = useAuth(); // context for authentication details
  const [networkList, setNetworkList] = useState([]); // to set network list
  const [isLoading, setIsLoading] = useState(false); //loading state of network list while fetching data
  const [headerData, setHeaderData] = useState({}); // to set auth token in herder to fetch API
  const [pageCount, setPageCount] = useState(1); // page count to fetch next page number list
  const [totalRecordCount, setTotalRecordCount] = useState(0); // total records count maintained
  let [isNext, isNextFunc] = useState(true); // flag to manage upcoming list of
  let [errorThrow, isErrorThrow] = useState(false); // flag against catch error if zone list api throws error
  const [open, setOpen] = useState(false);
  const [loaderSave, setLoaderSave] = useState(false); // to manage loader state which call on save network
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoader, setDialogLoader] = useState(false);
  const [searchText, setSearchText] = useState(""); // current "Search Network" text, kept across refreshes
  const [deactivateRow, setDeactivateRow] = useState(null); // network row in the Deactivate Mapping confirmation
  const [deactivateBusy, setDeactivateBusy] = useState(false);
  // only the latest list request may update the list (typing fast can overlap requests)
  const listSeq = useRef(0);
  // one "next page" request at a time for infinite scroll
  const loadingMore = useRef(false);
  // eslint-disable-next-line no-unused-vars
  const [hightLightFields, setHightLightFields] = useState({
    networkName: false,
    networkNumber: false,
  });
  /**
   * Request for network list fetch
   */
  let networkRequest = {
    Pagination: {
      PageSize: 25,
      PageNumber: pageCount,
    },
    ZoneId: props.id ? props.id : 0,
    NetworkName: "",
  };

  // call once component render
  useEffect(() => {
    let token =
      localStorageData !== null && localStorageData !== undefined
        ? localStorageData.authenticationToken.authToken
        : undefined;

    const header = {
      Authorization: `Bearer ${token ? token : bearerToken}`,
    };
    setHeaderData(header);
    if (!props.loadData) {
      fetchNetworkList(header);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.loadData]);

  /**
   *@description function to fetch network list
   * @param {*Bearer token for authentication} headerAuth
   */
  const fetchNetworkList = async (headerAuth) => {
    const seq = ++listSeq.current;
    setIsLoading(true);
    isErrorThrow(false);
    networkRequest.Pagination["PageNumber"] = 1;
    await networkConfigServices
      .getNetworkList(networkRequest, headerAuth)
      .then((response) => {
        if (seq !== listSeq.current) return; // a newer list request is in charge
        if (response && response.data) {
          if (response.data.httpStatusCode === 200) {
            setTotalRecordCount(response.data.totalRecords);
            props.getCountOfNetwork(response.data.totalRecords);
            setNetworkList(response.data.apiResponse);
            setIsLoading(false);
            isNextFunc(true);
          } else {
            props.getCountOfNetwork(0);
            setNetworkList([]);
            setIsLoading(false);
          }
        } else if (response.error) {
          props.getCountOfNetwork(0);
          ErrorHandler(response.error);
          isErrorThrow(true);
          setIsLoading(false);
        }
      })
      .catch((e) => {
        ErrorHandler(e);
        if (seq !== listSeq.current) return;
        setIsLoading(false);
        isErrorThrow(true);
      });
  };

  /**
   *@description function to fetch network list once page count is increase
   */
  const afterFetchMoreNetworkList = async () => {
    if (loadingMore.current) return;
    loadingMore.current = true;
    const seq = listSeq.current;
    networkRequest.Pagination["PageNumber"] = pageCount + 1;
    // next page of the current search, not of the full list
    networkRequest["NetworkName"] = searchText;
    isErrorThrow(false);
    await networkConfigServices
      .getNetworkList(networkRequest, headerData)
      .then((response) => {
        if (seq !== listSeq.current) return; // the list was reloaded meanwhile
        if (response && response.data) {
          if (response.data.apiResponse !== null) {
            let list = networkList.concat(response.data.apiResponse);
            setNetworkList(list);
            setPageCount(pageCount + 1);
          }
        } else if (response.error) {
          setIsLoading(false);
          isNextFunc(false);
        }
      })
      .catch((e) => {
        ErrorHandler(e);
        setIsLoading(false);
      })
      .finally(() => {
        loadingMore.current = false;
      });
  };

  const runSearch = (networkName) => {
    networkRequest["NetworkName"] = networkName;
    setPageCount(1);
    // Api calling function for search list by network name
    fetchNetworkList(headerData);
  };
  // search waits until typing pauses (one request instead of one per keystroke)
  const [debouncedSearch, cancelSearch] = useDebouncedCallback(runSearch, SEARCH_DEBOUNCE_MS);

  /**
   *@description function to fetch network list on search
   */
  const handleChange = (e) => {
    let networkName = e.target.value;
    setSearchText(networkName);
    if (networkName !== "" && networkName.length > 0) {
      debouncedSearch(networkName);
    } else if (networkName === "") {
      // cleared: show the full list straight away
      cancelSearch();
      setPageCount(1);
      fetchNetworkList(headerData);
    }
  };

  /**
   * @description call function on scroll event for fetch  zone list
   */
  const fetchMoreData = () => {
    if (networkList.length >= totalRecordCount) {
      isNextFunc(false);
      return;
    }
    afterFetchMoreNetworkList();
  };

  // on click of cancel btn dialog open
  const handleClose = () => {
    clearAndSetItem();
    setDialogOpen(true);
  };

  // on click of no or yes btn dialog close
  const handleModalClose = () => {
    setOpen(false);
    setDialogOpen(false);
  };

  // on click of add btn  add network modal open
  const handleOpen = () => {
    setOpen(true);
    clearAndSetItem();
    setDialogOpen(false);
  };

  // To close dialog box when form throws error
  const confirmModalClose = () => {
    setDialogOpen(false);
    // props.onModalClose();
  };

  /**
   * @description function call to set edit item data on modal open
   * @param {*data of network} item
   */
  const clearAndSetItem = (item) => {
    setHightLightFields({ networkName: false, networkNumber: false });
  };
  /**
   * @description submit function for create and edit zone with same API
   * @param {*form submitted values} values
   * @param {*on edit form user id send} id
   */
  const handleSubmit = async (values, id) => {
    let index =
      values.businessRule.length > 0
        ? values.businessRule.indexOf("Play Everything In SCH File")
        : -1;
    if (index > -1) {
      values.businessRule = ["Fill", "Bnus", "Paid"];
    }
    let rules =
      values.businessRule.length > 0
        ? values.businessRule.map((item, i) => {
            let ruleObj = CommonBusinessRulesList.find(
              (rule) => rule.ruleValue === item
            );
            let obj = {
              displayName:
                ruleObj && ruleObj.displayName ? ruleObj.displayName : item,
              ruleType: "SpotType",
              ruleValue: item,
            };
            return obj;
          })
        : CommonBusinessRulesList;
    if (dialogOpen === true) {
      setDialogLoader(true);
    } else {
      setLoaderSave(true);
    }
    setHightLightFields({ networkName: false, networkNumber: false });
    var data = JSON.parse(localStorage.getItem("userData"));
    let formData = {
      zone: {
        id: props.id ? props.id : 0,
      },
      id: id ? id : 0,
      networkNumber: values.networkNumber.toString(),
      networkName: values.networkName,
      mediaFieBasePath: values.mediaFieBasePath,
      createdBy: data.id,
      mznlId: values.mznlId ? values.mznlId : 0,
      businessRule: {
        rules: rules,
      },
    };
    const response = await networkConfigServices.CreateOrUpdateNetwork(formData, headerData);
    setLoaderSave(false);
    setDialogOpen(false);
    setDialogLoader(false);

    const feedback = formFeedback(classify(response), NETWORK_FORM_FIELDS);
    if (feedback.ok) {
      handleModalClose();
      clearAndSetItem({});
      fetchNetworkList(headerData);
      props.setShowMsgComp(true);
      props.setMsgContent({
        msg: id
          ? DESIGN_TEXT.networkUpdated
          : feedback.action === "attached"
          ? DESIGN_TEXT.networkAttached
          : DESIGN_TEXT.networkCreated,
        type: "success",
      });
    } else if (feedback.toast) {
      ErrorHandler(response);
      props.setShowMsgComp(true);
      props.setMsgContent({ msg: feedback.toast, type: "error" });
    }
    return feedback;
  };

  /**
   * @description reload the list after a change, keeping the current search text
   */
  const refreshNetworkList = () => {
    networkRequest["NetworkName"] = searchText;
    fetchNetworkList(headerData);
  };

  const showToast = (msg, type) => {
    props.setShowMsgComp(true);
    props.setMsgContent({ msg, type });
  };

  /**
   * @description confirm Deactivate Mapping (E5 design): removes this network from this zone only
   * (uses the row's mznlId, not the network id). Success → toast + refresh; already deactivated /
   * not found → toast + refresh; anything else → dialog closes, error toast, the row stays.
   */
  const confirmDeactivateMapping = async () => {
    const row = deactivateRow;
    setDeactivateBusy(true);
    const response = await networkConfigServices.DeactivateMapping(row.mznlId, headerData);
    const result = classify(response);
    setDeactivateBusy(false);
    setDeactivateRow(null);

    if (result.outcome === OUTCOME.SUCCESS) {
      showToast(DESIGN_TEXT.mappingDeactivated, "success");
      refreshNetworkList();
    } else if (
      result.outcome === OUTCOME.ALREADY_IN_STATE ||
      result.outcome === OUTCOME.NOT_FOUND
    ) {
      showToast(result.text, "error");
      refreshNetworkList();
    } else {
      ErrorHandler(response);
      showToast(result.outcome === OUTCOME.AUTH ? result.text : DESIGN_TEXT.generic, "error");
    }
  };

  const mappingLabel = (row) =>
    row
      ? props.zone
        ? DEACTIVATE_COPY.mapping.label(props.zone, row)
        : row.networkName
      : "";


  return (
    <div className="topPlacement">
      {/* E5 design header: title + Add Network tile button, search on the right */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          flexWrap: "wrap",
          mb: "24px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "24px" }}>
          <MainTitle title="Network Configuration" />
          <Box
            component="button"
            type="button"
            onClick={handleOpen}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "12px",
              fontSize: 13,
              cursor: "pointer",
              border: "none",
              background: "none",
              color: "#202020",
              p: 0,
              fontFamily: "inherit",
            }}
          >
            <Box
              component="span"
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                backgroundColor: "#fff",
                border: "1px solid #e3e3e8",
                boxShadow: "0 1px 3px rgba(16,24,40,.1)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#864797",
                fontSize: 14,
                boxSizing: "border-box",
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
            </Box>
            Add Network
          </Box>
        </Box>
        <SearchInputComp handleChange={handleChange} placeholderText={"Search Network"} />
      </Box>
      {isLoading ? (
        <CircularProgress size={40} color="info" className="circularLoader" />
      ) : (
        <Box
          sx={{
            flexGrow: 1,
            // E5 design: rows in a white rounded panel
            backgroundColor: "#fff !important",
            borderRadius: "12px",
            boxShadow: "0 1px 4px rgba(16,24,40,.12)",
            p: "24px 20px !important",
            border: "none",
          }}
          className="configCardLayout"
        >
          {errorThrow ? (
            ErrorCatchMsg()
          ) : networkList.length > 0 ? (
            <InfiniteScroll
              dataLength={networkList.length}
              next={fetchMoreData}
              hasMore={isNext}
              loader={
                <div className="infiniteLoader">
                  <CircularProgress size={40} className="infiniteLoader" />
                </div>
              }
            >
              {networkList.map((item, i) => {
                return (
                  <DropdownItem
                    title={item.networkName}
                    theme="dark"
                    data={item}
                    key={item.mznlId ? item.mznlId : i}
                    onClose={handleClose}
                    handleSubmit={handleSubmit}
                    loaderSave={loaderSave}
                    dialogOpen={dialogOpen}
                    handleModalClose={handleModalClose}
                    confirmModalClose={confirmModalClose}
                    dialogLoader={dialogLoader}
                    hightLightFields={hightLightFields}
                    onDeactivateMapping={(row) => setDeactivateRow(row)}
                    zone={props.zone}
                  />
                );
              })}
            </InfiniteScroll>
          ) : searchText ? (
            NoDataFoundComp()
          ) : (
            <div className="noActiveMappings" style={{ padding: "8px 4px 4px", color: "#666", fontSize: 12 }}>
              No active Network mappings in this Zone.
            </div>
          )}
        </Box>
      )}
      <ConfirmDialog
        open={!!deactivateRow}
        title={DEACTIVATE_COPY.mapping.title}
        label={mappingLabel(deactivateRow)}
        bullets={DEACTIVATE_COPY.mapping.bullets}
        busy={deactivateBusy}
        onConfirm={confirmDeactivateMapping}
        onCancel={() => setDeactivateRow(null)}
      />
      <DesignModal open={open} onClose={handleClose} labelledBy="network-form-title">
        <AddEditNetwork
          zone={props.zone}
          onClose={handleClose}
          handleSubmit={handleSubmit}
          loaderSave={loaderSave}
          dialogOpen={dialogOpen}
          handleModalClose={handleModalClose}
          hightLightFields={hightLightFields}
          confirmModalClose={confirmModalClose}
          dialogLoader={dialogLoader}
        />
      </DesignModal>
    </div>
  );
};

NetworkConfiguration.propTypes = {};

export default withRouter(NetworkConfiguration);
